import axios from 'axios';
import { paths } from '../routes/paths';
import { clearBrowserSession } from '../auth/browserSession';

const MAX_PAGES = 20;
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if it exists
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Backend responses are always { success, data } or { success: false, error } —
// architecture/api-contracts.md §0. Unwrap `data` here so every caller just gets the payload.
api.interceptors.response.use(
  (response) => {
    if (response.config.responseType === 'blob') return response.data; // a file (the receipt): not wrapped in { data }
    // Paged admin lists also return { meta: { page, limit, total } } — keep it on the array.
    const { data, meta } = response.data;
    if (meta && data && typeof data === 'object') Object.defineProperty(data, 'meta', { value: meta });
    return data;
  },
  async (error) => {
    const original = error.config;
    // Credential endpoints legitimately return 401 for bad input and must not trigger a refresh.
    const isCredentialCall = /\/api\/auth\/(login|register|social|refresh|logout|password\/(forgot|reset))/.test(original?.url || '');
    if (error.response?.status !== 401 || !original || original._retried || isCredentialCall) {
      return Promise.reject(error);
    }
    // Access tokens live 15 minutes. On a 401, swap the refresh token for a new pair once and
    // replay the request, so a long-lived admin or organiser session keeps working. If the
    // refresh itself fails, the session is really over: clear it and go to the login page.
    original._retried = true;
    try {
      const { accessToken } = await refreshSession();
      original.headers.Authorization = `Bearer ${accessToken}`;
      return api(original);
    } catch (refreshError) {
      // Only a definite refusal ends the session: the server said the refresh token is not valid (401/400), or there is none to try.
      const status = refreshError.response?.status;
      if (status === 401 || status === 400 || refreshError.message === 'No refresh token') {
        clearSession();
        if (!window.location.pathname.includes('login')) window.location.assign(paths.login);
        return Promise.reject(error); // the session is over: the original 401
      }
      // The server or the network could not answer (503, another 5xx, no connection, a timeout). Reject with THAT error, not the original 401:
      // callers such as AuthContext read a 401 as "signed out", and an outage must not sign anyone out. The tokens stay; they retry later.
      return Promise.reject(refreshError);
    }
  }
);

// One refresh at a time: refresh tokens rotate, so two parallel refreshes with the same token
// would make the second one fail and log the user out.
let refreshing = null;
function refreshSession() {
  if (!refreshing) {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return Promise.reject(new Error('No refresh token'));
    refreshing = axios
      .post(`${API_URL}/api/auth/refresh`, { refreshToken })
      .then((res) => {
        const tokens = res.data.data;
        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        return tokens;
      })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

const clearSession = clearBrowserSession;

// Auth API — architecture/api-contracts.md §1. Shared by all 4 roles (attendee, organiser,
// staff, admin) — one login/forgot/reset for everyone, never a per-role duplicate.
export const authAPI = {
  // Sign-up verifies the phone first: nothing is created until the code is right.
  logout: (refreshToken) => api.post('/api/auth/logout', { refreshToken }),
  registerRequestOtp: (data) => api.post('/api/auth/register/otp/request', data),
  registerResendOtp: (email) => api.post('/api/auth/register/otp/resend', { email }),
  registerVerifyOtp: (data) => api.post('/api/auth/register/otp/verify', data),
  login: (data) => api.post('/api/auth/login', data),
  // Google / Facebook: which buttons work (set in Admin > Settings), and the sign-in itself.
  socialConfig: () => api.get('/api/auth/social/config'),
  socialLogin: (provider, data) => api.post(`/api/auth/social/${provider}`, data),
  getMe: () => api.get('/api/auth/me'),
  updateMe: (data) => api.patch('/api/auth/me', data),
  changePassword: (data) => api.post('/api/auth/password/change', data),
  refresh: (refreshToken) => api.post('/api/auth/refresh', { refreshToken }),
  forgotPassword: (email) => api.post('/api/auth/password/forgot', { email }),
  resetPassword: (token, newPassword) => api.post('/api/auth/password/reset', { token, newPassword }),
};

// Events API — architecture/api-contracts.md §3. Same endpoints the mobile app's Discover
// screen will call — see [[feedback_shared_api_web_mobile]] in project memory: never fork
// this into a web-only copy.
export const eventsAPI = {
  getAll: (params) => api.get('/api/events', { params }),
  getMine: (params) => api.get('/api/events', { params: { ...params, mine: true } }),
  // How many events each When / Price option would list (the counts in the Filters card)
  facets: (params) => api.get('/api/events/facets', { params }),
  // Every page, not just the first (the API caps a page at 50). Stops at MAX_PAGES so a huge list cannot hang the screen.
  getAllPages: async (params) => {
    const out = [];
    let cursor;
    for (let i = 0; i < MAX_PAGES; i += 1) {
      const page = await api.get('/api/events', { params: { ...params, pageSize: 50, cursor } });
      out.push(...page);
      cursor = page.meta?.nextCursor;
      if (!page.meta?.hasMore || !cursor) break;
    }
    return out;
  },
  getMineAllPages: async (params) => {
    const out = [];
    for (let page = 1; page <= MAX_PAGES; page += 1) {
      const rows = await api.get('/api/events', { params: { ...params, mine: true, pageSize: 50, page } });
      out.push(...rows);
      if (!rows.meta?.hasMore) break;
    }
    return out;
  },
  getOne: (id) => api.get(`/api/events/${id}`),
  create: (data) => api.post('/api/events', data),
  update: (id, data) => api.patch(`/api/events/${id}`, data),
  publish: (id) => api.post(`/api/events/${id}/publish`),
  delete: (id) => api.delete(`/api/events/${id}`),
  // Cover / gallery images. Sent as multipart, so the client's default JSON header is replaced.
  uploadImage: (id, file, isCover = false) => {
    const form = new FormData();
    form.append('image', file);
    if (isCover) form.append('isCover', 'true');
    return api.post(`/api/events/${id}/images`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteImage: (id, index) => api.delete(`/api/events/${id}/images/${index}`),
};

// Ticket Types API — architecture/api-contracts.md §4
export const ticketTypesAPI = {
  getForEvent: (eventId) => api.get(`/api/events/${eventId}/ticket-types`),
  create: (eventId, data) => api.post(`/api/events/${eventId}/ticket-types`, data),
  update: (id, data) => api.patch(`/api/ticket-types/${id}`, data),
  delete: (id) => api.delete(`/api/ticket-types/${id}`),
};

// Lookups API — categories/locations/currencies for filters, the organiser create-event
// form, and money formatting (see src/utils/money.js — never hardcode a currency symbol).
// Orders: free-ticket checkout (a guest gives an email, a signed-in buyer needs nothing more)
export const ordersAPI = {
  // `idempotencyKey` makes a retry (double click, dropped connection) return the same order, never a second one.
  create: (data, idempotencyKey) => api.post('/api/orders', data, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined),
  getOne: (id) => api.get(`/api/orders/${id}`),
  // pay a waiting order with a saved card: answers { paymentMethod }, the id to confirm the payment with
  useSavedCard: (id, paymentMethodId) => api.post(`/api/orders/${id}/saved-card`, { paymentMethodId }),
  // Refunds the buyer asks for on their own order
  receipt: (id) => api.get(`/api/orders/${id}/receipt`, { responseType: 'blob' }), // the PDF
  refundQuote: (id, ticketIds) => api.get(`/api/orders/${id}/refund-quote`, { params: ticketIds?.length ? { ticketIds: ticketIds.join(',') } : {} }),
  requestRefund: (id, body) => api.post(`/api/orders/${id}/refund-requests`, body),
  refundRequests: (id) => api.get(`/api/orders/${id}/refund-requests`),
  list: (params) => api.get('/api/orders', { params }),
  // a guest finds their order with the order number and the email they bought with
  guestLookup: (orderNumber, email) => api.post('/api/orders/guest-lookup', { orderNumber, email }),
  // After paying by card: asks Stripe what happened to this order's payment (it never trusts the browser). { orderNumber, guestEmail? }
  confirmPayment: (data) => api.post('/api/orders/confirm-payment', data),
  // the platform fee, so the checkout can show the total before the order exists
  paymentOptions: () => api.get('/api/orders/payment-options'),
};

// "My tickets": one card per order line. tab = 'upcoming' | 'past'
// Door check-in (staff mode). Everything is for the caller's own organiser; the server works that out from their role.
export const checkinAPI = {
  events: () => api.get('/api/checkin/events'),
  summary: (eventId) => api.get(`/api/checkin/events/${eventId}`),
  // { result: 'checked_in', ticket, counts } or { result: 'not_valid', reason, message, ... }
  scan: (eventId, code) => api.post(`/api/checkin/events/${eventId}/scan`, { code }),
  attendee: (eventId, orderId) => api.get(`/api/checkin/events/${eventId}/attendees/${orderId}`),
  searchAttendees: (eventId, q) => api.get(`/api/checkin/events/${eventId}/attendees`, { params: { q } }),
  checkInTickets: (eventId, ticketIds) => api.post(`/api/checkin/events/${eventId}/check-in`, { ticketIds }),
};

export const ticketsAPI = {
  list: (tab) => api.get('/api/tickets', { params: { tab } }),
};

// Privacy and data rights (GDPR): the cookie choice, a copy of my data by email, closing my account.
export const privacyAPI = {
  getConsent: () => api.get('/api/privacy/consent'),
  saveConsent: (choice) => api.post('/api/privacy/consent', choice), // { analytics, marketing }
  exportData: () => api.post('/api/privacy/export'),
  deleteAccount: (body) => api.delete('/api/privacy/account', { data: body }), // { password? }
};

// Saved cards (Figma "Payment methods"). Add card: startSetup, Stripe's form confirms it, then add(setupIntentId).
export const paymentMethodsAPI = {
  list: () => api.get('/api/payment-methods'),
  startSetup: () => api.post('/api/payment-methods/setup-intent'), // { clientSecret, publishableKey }
  add: (setupIntentId) => api.post('/api/payment-methods', { setupIntentId }),
  setDefault: (id) => api.post(`/api/payment-methods/${id}/default`), // answers the cards, the new default first
  remove: (id) => api.delete(`/api/payment-methods/${id}`), // answers the cards left
};

// CMS pages (privacy policy, terms and conditions, ...): the published ones, for the footer and the page itself.
export const pagesAPI = {
  list: () => api.get('/api/pages'),
  get: (slug) => api.get(`/api/pages/${slug}`),
};

// In-app notifications (F16). `before` is the nextBefore of the previous page.
export const notificationsAPI = {
  list: (params) => api.get('/api/notifications', { params }),
  markRead: (id) => api.post(`/api/notifications/${id}/read`),
  markAllRead: () => api.post('/api/notifications/read-all'),
  getPreferences: () => api.get('/api/notifications/preferences'),
  savePreferences: (prefs) => api.patch('/api/notifications/preferences', prefs),
};

export const lookupsAPI = {
  getCategories: () => api.get('/api/categories'),
  getLocations: () => api.get('/api/locations'),
  getMapsConfig: () => api.get('/api/maps/config'),
  getCurrencies: () => api.get('/api/currencies'),
  getAgeRestrictions: () => api.get('/api/age-restrictions'),
};

// Organizer API — architecture/api-contracts.md §8
export const organizerAPI = {
  getSalesReport: (organizerId, params) => api.get(`/api/organizers/${organizerId}/reports/sales`, { params }), // { range, eventId? }
  // Sign-up is two steps: apply sends a text code and an email code; verify creates the account.
  apply: (data) => api.post('/api/organizers/apply', data),
  resendApplyCodes: (workEmail) => api.post('/api/organizers/apply/resend', { workEmail }),
  verifyApply: (data) => api.post('/api/organizers/apply/verify', data),
  // KYC (Verification screen). Files are sent as multipart; the browser sets the boundary itself.
  getVerification: (organizerId) => api.get(`/api/organizers/${organizerId}/verification`),
  uploadDocument: (organizerId, typeCode, file) => {
    const form = new FormData();
    form.append('typeCode', String(typeCode));
    form.append('document', file);
    // The client defaults to JSON; a file upload must go out as multipart (the browser adds the boundary).
    return api.post(`/api/organizers/${organizerId}/documents`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteDocument: (organizerId, docId) => api.delete(`/api/organizers/${organizerId}/documents/${docId}`),
  // Company profile: details and logo
  updateCompany: (organizerId, data) => api.patch(`/api/organizers/${organizerId}`, data),
  uploadLogo: (organizerId, file) => {
    const form = new FormData();
    form.append('logo', file);
    return api.post(`/api/organizers/${organizerId}/logo`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  removeLogo: (organizerId) => api.delete(`/api/organizers/${organizerId}/logo`),
  // Owner manages their own staff — staff are never self-service.
  listStaff: (organizerId) => api.get(`/api/organizers/${organizerId}/staff`),
  inviteStaff: (organizerId, data) => api.post(`/api/organizers/${organizerId}/staff`, data),
  updateStaff: (organizerId, userId, data) => api.patch(`/api/organizers/${organizerId}/staff/${userId}`, data),
  revokeStaff: (organizerId, userId) => api.delete(`/api/organizers/${organizerId}/staff/${userId}`),
  // a set-password link for staff who never signed in, while no mail server is set up: { link, expiresInMinutes }
  staffInviteLink: (organizerId, userId) => api.post(`/api/organizers/${organizerId}/staff/${userId}/invite-link`),
  // Stripe Connect: where the organiser's ticket money is paid out. They never handle an account id.
  listOrders: (organizerId, params) => api.get(`/api/organizers/${organizerId}/orders`, { params }),
  listRefunds: (organizerId, params) => api.get(`/api/organizers/${organizerId}/refunds`, { params }),
  approveRefund: (organizerId, id) => api.post(`/api/organizers/${organizerId}/refunds/${id}/approve`),
  rejectRefund: (organizerId, id, body) => api.post(`/api/organizers/${organizerId}/refunds/${id}/reject`, body),
  // { type: 'full' | 'partial', amount? } (amount in minor units)
  issueRefund: (organizerId, orderId, body) => api.post(`/api/organizers/${organizerId}/orders/${orderId}/refunds`, body),
  getPayouts: (organizerId) => api.get(`/api/organizers/${organizerId}/payouts`),
  getStripe: (organizerId) => api.get(`/api/organizers/${organizerId}/stripe`),
  connectStripe: (organizerId, returnUrl) => api.post(`/api/organizers/${organizerId}/stripe/connect`, { returnUrl }),
};

// Admin API — architecture/api-contracts.md §9
export const adminAPI = {
  listStaff: (params) => api.get('/api/admin/staff', { params }), // { organizerId?, q?, page?, limit? }
  revokeStaff: (userId, organizerId) => api.delete(`/api/admin/staff/${userId}`, { params: { organizerId } }),
  restoreStaff: (userId, organizerId) => api.post(`/api/admin/staff/${userId}/restore`, {}, { params: { organizerId } }),
  listOrganizers: (params) => api.get('/api/admin/organizers', { params }),
  getOrganizer: (id) => api.get(`/api/admin/organizers/${id}`),
  // The organiser's uploaded KYC documents; each carries a viewUrl that expires after 5 minutes.
  listOrganizerDocuments: (id) => api.get(`/api/admin/organizers/${id}/documents`),
  // statusCode 1 = verified, 2 = rejected (rejectionReason required)
  reviewOrganizerDocument: (id, docId, body) => api.patch(`/api/admin/organizers/${id}/documents/${docId}`, body),
  approveOrganizer: (id, body) => api.post(`/api/admin/organizers/${id}/approve`, body),
  rejectOrganizer: (id, body) => api.post(`/api/admin/organizers/${id}/reject`, body),
  suspendOrganizer: (id, body) => api.post(`/api/admin/organizers/${id}/suspend`, body),
  reactivateOrganizer: (id, body) => api.post(`/api/admin/organizers/${id}/reactivate`, body),
  listUsers: (params) => api.get('/api/admin/users', { params }),
  suspendUser: (id) => api.post(`/api/admin/users/${id}/suspend`),
  reactivateUser: (id) => api.post(`/api/admin/users/${id}/reactivate`),
  listEvents: (params) => api.get('/api/admin/events', { params }),
  moderateEvent: (id, body) => api.patch(`/api/admin/events/${id}/status`, body),
  listOrders: (params) => api.get('/api/admin/orders', { params }),
  getSalesReport: (params) => api.get('/api/admin/reports/sales', { params }),
  listRefunds: (params) => api.get('/api/admin/refunds', { params }),
  approveRefund: (id) => api.post(`/api/admin/refunds/${id}/approve`),
  rejectRefund: (id, body) => api.post(`/api/admin/refunds/${id}/reject`, body),
  listAuditLogs: (params) => api.get('/api/admin/audit-logs', { params }),
  listCategories: () => api.get('/api/admin/categories'),
  listPages: () => api.get('/api/admin/pages'),
  getPage: (id) => api.get(`/api/admin/pages/${id}`),
  createPage: (body) => api.post('/api/admin/pages', body),
  updatePage: (id, body) => api.patch(`/api/admin/pages/${id}`, body),
  deletePage: (id) => api.delete(`/api/admin/pages/${id}`),
  createCategory: (body) => api.post('/api/admin/categories', body),
  updateCategory: (id, body) => api.patch(`/api/admin/categories/${id}`, body),
  deleteCategory: (id) => api.delete(`/api/admin/categories/${id}`),
  getFees: () => api.get('/api/admin/settings/fees'),
  setFees: (body) => api.put('/api/admin/settings/fees', body),
  // Stripe / Twilio (SMS) / email credentials. Secrets are write-only: the API never returns them.
  getIntegrations: () => api.get('/api/admin/settings/integrations'),
  saveIntegration: (name, body) => api.put(`/api/admin/settings/integrations/${name}`, body),
};

// The signed-in user's own profile lives in authAPI (getMe / updateMe): one API for every role.

export default api;
